import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { MyResourceKey, Statistics } from '@/types';
import { UseExtendQueryOptions, useQuery } from '@/utils/api';
import { sToMs } from '@/utils/date.util';

interface UseCountsProps<ResourceKey extends MyResourceKey> {
  resource: ResourceKey;
  // query to filter the resource data, if not provided, it will fetch all data
  query?: string;
}

export const useCounts = <ResourceKey extends MyResourceKey>(
  props: UseCountsProps<ResourceKey>,
  options?: UseExtendQueryOptions<Statistics>
) => {
  const axios = useAxios({ email: options?.email });

  const query = useQuery<Statistics>({
    ...options,
    defaultErrorMessage: `Failed to fetch ${props.resource} counts`,
    refetchInterval: options?.enabled !== false ? sToMs(30) : false,
    refetchIntervalInBackground: false, // Stop polling when tab inactive
    queryKey: useGetUserKey(
      getQueryKey.getCounts(props.resource, props.query),
      false,
      options?.email
    ),
    queryFn: async () => {
      const keyParam = props.query ? props.query : `#${props.resource}`;

      const { data } = await axios.get(`/my/count`, {
        params: {
          key: keyParam,
        },
      });

      // remap empty key with 'cloud'
      if ('' in data) {
        data.cloud = data[''];
        delete data[''];
      }
      return data as Statistics;
    },
  });

  // Pre-computed total count by summing all numeric values in the statistics
  // This aggregates status counts (A, F, FR, P, etc.) into a single total
  const totalCount = useMemo(() => {
    if (!query.data) return undefined;
    return Object.values(query.data).reduce((sum: number, value) => {
      return typeof value === 'number' ? sum + value : sum;
    }, 0);
  }, [query.data]);

  return {
    ...query,
    totalCount,
  };
};
